import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaChartLine, FaLightbulb, FaRoad, FaBook, FaIndustry, FaChartBar, FaSpinner, FaTimes, FaInfoCircle, FaExclamationTriangle, FaClock, FaSyncAlt, FaFilePdf, FaFileExport } from 'react-icons/fa';

// Report Generator
import { generateIndustryAnalysisReport } from '../../utils/reportGenerator';
import { generateModernIndustryAnalysisReport } from '../../utils/modernReportGenerator';

// Context
import AuthContext from '../../context/AuthContext';

// Components
import CompactOrgSelector from '../organization/CompactOrgSelector';
import CompactAssessmentSelector from '../assessment/CompactAssessmentSelector';
import LoadingModal from '../common/LoadingModal';

const IndustryAnalysisPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [organizationData, setOrganizationData] = useState(null);
  const [industryData, setIndustryData] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [industryComparisons, setIndustryComparisons] = useState(null);
  const [detailedInsights, setDetailedInsights] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [showStepDetails, setShowStepDetails] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemDetails, setShowItemDetails] = useState(false);
  const [detailPanelContent, setDetailPanelContent] = useState(null);
  const [insightsGenerated, setInsightsGenerated] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showAssessmentPanel, setShowAssessmentPanel] = useState(true);
  
  // Store insights by assessment ID to avoid regenerating them
  const [assessmentInsights, setAssessmentInsights] = useState({});
  
  // State for AI-generated content
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState({});
  
  // Maturity levels definition based on the provided image
  const maturityLevels = [
    { level: 0, name: 'None', color: '#333333', range: [0, 1] },
    { level: 1, name: 'Initial', color: '#990000', range: [1, 20] },
    { level: 2, name: 'Defined', color: '#D2BA7B', range: [20, 50] },
    { level: 3, name: 'Managed', color: '#243444', range: [50, 75] },
    { level: 4, name: 'Compliant', color: '#2980B9', range: [75, 91] },
    { level: 5, name: 'Optimized', color: '#27AE60', range: [91, 101] }
  ];
  
  // Helper function to normalize scores correctly
  const normalizeScore = (score) => {
    if (score === undefined || score === null) return 0;
    
    // For very low scores (0-1%), ensure they don't get rounded to 100%
    if (score >= 0 && score < 0.01) {
      return Math.max(Math.round(score * 100), 0);
    }
    
    // Normal score normalization
    return score < 1 ? Math.round(score * 100) : Math.round(score);
  };
  
  // Token for API calls
  const token = localStorage.getItem('token');
  
  // Fetch organization data on initial load
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        
        // Check if there's a selected organization in localStorage from dashboard
        let orgToUse = null;
        let assessmentToUse = null;
        
        // Try to get organization from storage (sessionStorage first, then localStorage)
        try {
          // First check sessionStorage for the most recent selection
          const currentSelectedOrgData = sessionStorage.getItem('currentSelectedOrganization');
          if (currentSelectedOrgData) {
            orgToUse = JSON.parse(currentSelectedOrgData);
            console.log(`Found organization in sessionStorage: ${orgToUse.name}`);
          }
          
          // Get dashboardState from localStorage for both organization and assessment
          const dashboardState = localStorage.getItem('dashboardState');
          let parsedDashboardState = null;
          
          if (dashboardState) {
            try {
              parsedDashboardState = JSON.parse(dashboardState);
            } catch (parseErr) {
              console.error('Error parsing dashboardState:', parseErr);
            }
          }
          
          // If not found in sessionStorage, check dashboardState
          if (!orgToUse && parsedDashboardState) {
            if (parsedDashboardState.selectedOrganization && parsedDashboardState.selectedOrganization._id) {
              orgToUse = parsedDashboardState.selectedOrganization;
              console.log(`Found organization in dashboardState: ${orgToUse.name}`);
            }
          }
          
          // If still not found, try lastSelectedOrganization
          if (!orgToUse) {
            const lastSelectedOrgData = localStorage.getItem('lastSelectedOrganization');
            if (lastSelectedOrgData) {
              orgToUse = JSON.parse(lastSelectedOrgData);
              console.log(`Found organization in lastSelectedOrganization: ${orgToUse.name}`);
            }
          }
          
          // Try to get assessment from storage (sessionStorage first, then localStorage)
          // First check sessionStorage for most recent selection
          const currentSelectedAssessmentData = sessionStorage.getItem('currentSelectedAssessment');
          if (currentSelectedAssessmentData) {
            assessmentToUse = JSON.parse(currentSelectedAssessmentData);
            console.log(`Found assessment in sessionStorage: ${assessmentToUse.title || assessmentToUse.name}`);
          }
          
          // If not found in sessionStorage, check dashboardState
          if (!assessmentToUse && parsedDashboardState) {
            if (parsedDashboardState.selectedAssessment) {
              assessmentToUse = parsedDashboardState.selectedAssessment;
              console.log(`Found assessment in dashboardState: ${assessmentToUse.title || assessmentToUse.name}`);
            }
          }
          
          // If still not found, try lastSelectedAssessment
          if (!assessmentToUse) {
            const lastSelectedAssessmentData = localStorage.getItem('lastSelectedAssessment');
            if (lastSelectedAssessmentData) {
              assessmentToUse = JSON.parse(lastSelectedAssessmentData);
              console.log(`Found assessment in lastSelectedAssessment: ${assessmentToUse.title || assessmentToUse.name}`);
            }
          }
        } catch (parseErr) {
          console.error('Error parsing localStorage data:', parseErr);
        }
        
        // If no organization found in localStorage, fetch the current one
        if (!orgToUse) {
          const orgResponse = await axios.get('/api/organizations/current');
          orgToUse = orgResponse.data;
          console.log(`Using current organization: ${orgToUse.name}`);
        }
        
        // Set the organization
        setOrganizationData(orgToUse);
        setSelectedOrganization(orgToUse);
        
        // If we have an organization and assessment, fetch assessments and select the one from localStorage
        if (orgToUse && orgToUse._id) {
          try {
            const assessmentsRes = await axios.get(`/api/assessments?organizationId=${orgToUse._id}`);
            const orgAssessments = assessmentsRes.data || [];
            setAssessments(orgAssessments);
            
            // If we have a selected assessment from localStorage and it belongs to this organization, select it
            if (assessmentToUse && assessmentToUse._id) {
              // Check if the assessment belongs to the selected organization
              const matchingAssessment = orgAssessments.find(a => a._id === assessmentToUse._id);
              if (matchingAssessment) {
                console.log(`Selecting assessment from localStorage: ${matchingAssessment.title || matchingAssessment.name}`);
                // Set the selected assessment directly instead of using handleSelectAssessment
                // to avoid dependency issues in useEffect
                setSelectedAssessment(matchingAssessment);
                
                // Generate insights for the assessment if needed
                if (!assessmentInsights[matchingAssessment._id]) {
                  await generateInsights(matchingAssessment);
                } else {
                  // Use cached insights
                  const cachedInsights = assessmentInsights[matchingAssessment._id];
                  setIndustryData(cachedInsights.industryData);
                  setIndustryComparisons(cachedInsights.industryComparisons);
                  setAiAnalysis(cachedInsights.aiAnalysis);
                  setDetailedInsights(cachedInsights.detailedInsights);
                  setInsightsGenerated(true);
                }
              }
            }
          } catch (assessErr) {
            console.error('Error fetching assessments:', assessErr);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching initial data:', err);
        setError('Failed to load organization data');
        setLoading(false);
      }
    };
    
    fetchInitialData();
  }, []);
  
  // Handle organization selection
  const handleSelectOrganization = async (organization) => {
    setSelectedOrganization(organization);
    setSelectedAssessment(null); // Reset selected assessment when org changes
    setAiAnalysis({}); // Reset AI analysis
    
    try {
      setLoading(true);
      
      // Save selected organization to localStorage and sessionStorage for persistence
      try {
        // Update dashboardState
        const dashboardState = JSON.parse(localStorage.getItem('dashboardState') || '{}');
        dashboardState.selectedOrganization = organization;
        localStorage.setItem('dashboardState', JSON.stringify(dashboardState));
        
        // Save as the last selected organization
        localStorage.setItem('lastSelectedOrganization', JSON.stringify(organization));
        
        // Save to sessionStorage for immediate persistence
        sessionStorage.setItem('currentSelectedOrganization', JSON.stringify(organization));
        
        console.log('Saved selected organization to storage:', organization.name);
      } catch (err) {
        console.error('Error saving organization to storage:', err);
      }
      
      // Fetch assessments for the selected organization
      const assessmentsResponse = await axios.get(`/api/assessments?organizationId=${organization._id}`);
      const completedAssessments = assessmentsResponse.data.filter(a => a.status === 'completed');
      setAssessments(completedAssessments);
      
      // Find the most recent completed assessment
      if (completedAssessments.length > 0) {
        // Sort by date descending
        const sorted = [...completedAssessments].sort((a, b) => 
          new Date(b.completedAt || b.updatedAt) - new Date(a.completedAt || a.updatedAt)
        );
        const mostRecentAssessment = sorted[0];
        
        // Get industry type from organization or selected assessment
        const industryType = organization.industryType || mostRecentAssessment.industryType || 'Other';
        
        // Generate mock industry data (will be replaced with real data from API)
        const mockIndustryData = generateMockIndustryData(industryType);
        setIndustryData(mockIndustryData);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching organization data:', err);
      setError('Failed to load organization data');
      setLoading(false);
    }
  };
  
  // Handle assessment selection and fetch AI insights
  const handleSelectAssessment = async (assessment) => {
    if (!assessment) return;
    
    setSelectedAssessment(assessment);
    
    // Save selected assessment to localStorage and sessionStorage for persistence
    try {
      // Save to sessionStorage for persistence
      sessionStorage.setItem('currentSelectedAssessment', JSON.stringify(assessment));
    } catch (err) {
      console.error('Error saving assessment to storage:', err);
    }
    
    // Generate insights for the selected assessment
    await generateInsights(assessment);
    
    // Hide assessment panel after selection on mobile
    if (window.innerWidth < 768) {
      setShowAssessmentPanel(false);
    }
  };
  
  // Handle regenerating insights with OpenAI
  const handleRegenerateInsights = async () => {
    if (!selectedAssessment) {
      setAiError('Please select an assessment before regenerating insights');
      return;
    }
    
    // Show loading modal immediately
    setAiLoading(true);
    setAiError(null);
    
    try {
      // Clear any cached implementation steps or insights
      if (selectedAssessment._id && assessmentInsights[selectedAssessment._id]) {
        // Create a new copy without the cached data
        const updatedInsights = { ...assessmentInsights };
        delete updatedInsights[selectedAssessment._id];
        setAssessmentInsights(updatedInsights);
      }
      
      // Add a small delay to ensure the loading modal is visible
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Force a refresh of the insights by passing true as the second parameter
      await generateInsights(selectedAssessment, true);
    } catch (error) {
      console.error('Error regenerating insights:', error);
      setAiError('Failed to regenerate insights. Please try again.');
      setAiLoading(false);
    }
  };
  
  // Generate insights for the selected assessment
  const generateInsights = async (assessment, forceRefresh = false) => {
    if (!assessment) {
      console.error('Cannot generate insights: assessment is null or undefined');
      return;
    }

    try {
      // Only set loading state if not already set (allows handleRegenerateInsights to control the loading state)
      if (!forceRefresh) {
        setAiLoading(true);
        setAiError(null);
      }
      
      // Check if we already have stored insights for this assessment and not forcing refresh
      if (!forceRefresh && assessment._id && assessmentInsights[assessment._id]) {
        console.log('Using stored insights for assessment:', assessment._id);
        const storedInsights = assessmentInsights[assessment._id];
        
        // Set the stored data
        setIndustryData(storedInsights.industryData);
        setIndustryComparisons(storedInsights.industryComparisons);
        setAiAnalysis(storedInsights.aiAnalysis);
        setDetailedInsights(storedInsights.detailedInsights);
        
        // Mark insights as generated and hide loading
        setInsightsGenerated(true);
        setAiLoading(false);
        return;
      }
      
      // Get industry type from organization or selected assessment with proper null checks
      const industryType = (selectedOrganization && selectedOrganization.industryType) || 
                          (assessment && assessment.industryType) || 
                          (user && user.industryType) || 
                          'Other';
      
      console.log(`Using industry type: ${industryType} for assessment analysis`);
      
      // Generate mock industry data (will be replaced with real data from API)
      const mockIndustryData = generateMockIndustryData(industryType);
      setIndustryData(mockIndustryData);
      
      // Safely get the assessment score with fallback
      const assessmentScore = assessment.score !== undefined ? assessment.score : 0.5;
      
      // Normalize the score for consistent display
      const normalizedScore = assessmentScore < 1 ? 
        Math.round(assessmentScore * 100) : 
        Math.round(assessmentScore);
      
      // Get the maturity level based on the score
      const maturityLevel = getMaturityLevel(normalizedScore);
      
      // Generate industry comparisons based on the selected assessment
      // Calculate percentile based on the normalized score
      const calculatedPercentile = Math.min(Math.round((normalizedScore / 55) * 100), 99);
      
      const mockComparisons = {
        overallComparison: {
          organizationScore: normalizedScore,
          industryAverage: 55, // Industry average
          percentile: calculatedPercentile, // Calculate percentile based on actual score
          maturityLevel: maturityLevel,
          comparisonText: normalizedScore < 55 
            ? `Your organization is performing below the industry average of 55%.`
            : `Your organization is performing above the industry average of 55%.`
        },
        categoryComparisons: generateCategoryComparisons(industryType, assessment)
      };
      setIndustryComparisons(mockComparisons);
      
      let aiData = {};
      let insightsData = null;
      
      // First, try to fetch stored analysis from the database
      try {
        if (assessment._id) {
          // Check if we're forcing a refresh
          if (!forceRefresh) {
            try {
              const storedResponse = await axios.get(`/api/industry-analysis/stored/${assessment._id}`, 
                { headers: { 'x-auth-token': token } }
              );
              
              if (storedResponse.data && Object.keys(storedResponse.data).length > 0) {
                console.log('Found stored analysis in database');
                aiData = storedResponse.data;
                setAiAnalysis(aiData);
                
                // Skip the OpenAI call if we have stored data
                const hasValidData = aiData && (aiData.detailedAnalysis || aiData.industryTrends);
                if (hasValidData) {
                  console.log('Using stored analysis data');
                  // Skip the OpenAI call by returning early
                  return;
                }
              }
            } catch (storageError) {
              console.log('No stored analysis found or error fetching it:', storageError.message);
              // Continue to OpenAI call
            }
          } else {
            console.log('Forcing refresh of analysis with OpenAI');
          }
          
          // Generate new analysis with OpenAI
          console.log('Generating new analysis with OpenAI');
          const response = await axios.post('/api/industry-analysis/comprehensive', {
            assessmentId: assessment._id
          }, { headers: { 'x-auth-token': token } });
          
          // Process the API response
          aiData = response.data;
          
          // Fix any score formatting issues in the API response
          if (aiData.detailedAnalysis && typeof aiData.detailedAnalysis === 'string') {
            // First, fix the overall score with more comprehensive regex
            aiData.detailedAnalysis = aiData.detailedAnalysis
              // Fix any extremely high percentages (over 100%)
              .replace(/\b([1-9]\d{2,}|[1-9]\d{1,2}0{1,2})%\b/g, `${normalizedScore}%`) 
              // Replace standalone 0% but not ranges like "0% - 20%"
              .replace(/\b0%\b(?!\s*-\s*\d)/g, `${normalizedScore}%`) 
              // Replace any overall score mentions with the correct score
              .replace(/overall\s+resilience\s+posture\s+score\s+of\s+\d+\.?\d*%/gi, `overall resilience posture score of ${normalizedScore}%`)
              .replace(/overall\s+score\s+of\s+\d+\.?\d*%/gi, `overall score of ${normalizedScore}%`)
            
            // Now, completely rewrite the detailed analysis to remove all category score mentions
            // This is a more aggressive approach but necessary to ensure all category scores are removed
            
            // Check if the text contains problematic category score mentions
            if (aiData.detailedAnalysis.includes('Each category') || 
                aiData.detailedAnalysis.includes('scored 0%') || 
                aiData.detailedAnalysis.includes('across all categories') ||
                aiData.detailedAnalysis.includes('each scoring') ||
                aiData.detailedAnalysis.includes('all assessed categories') ||
                aiData.detailedAnalysis.includes('in all categories')) {
              
              // Create a completely new analysis without any category scores
              const newAnalysis = `The organization's resilience posture requires significant improvement, with an overall score of ${normalizedScore}%. This indicates a need for targeted interventions in key resilience domains. 

The current maturity level suggests opportunities to strengthen the foundational elements of the organization's resilience framework. Developing structured policies, protocols, and practices is necessary for effective resilience management and operational continuity. 

To enhance resilience capabilities, the organization should focus on these key areas:

• Establishing a comprehensive governance structure aligned with business objectives
• Implementing robust risk assessment and management processes
• Developing detailed business continuity and disaster recovery plans
• Creating effective incident response strategies with clear roles and responsibilities
• Building resilience awareness and capabilities throughout the organization

These improvements will better position the organization to navigate disruptions while maintaining operational continuity in alignment with industry best practices.`;
              
              // Replace the entire analysis with our new version
              aiData.detailedAnalysis = newAnalysis;
            }
          }
          
          setAiAnalysis(aiData);
        }
      } catch (apiErr) {
        console.error('Error fetching AI insights:', apiErr);
        setAiError('Failed to load insights from AI. Please try again later.');
        
        // Generate fallback insights if API call fails
        const fallbackMaturityLevel = assessment.maturityLevel || maturityLevel || 'Defined';
        insightsData = generateDetailedInsights(
          fallbackMaturityLevel,
          industryType,
          assessmentScore,
          assessment
        );
        setDetailedInsights(insightsData);
      }
      
      // Store insights for this assessment
      if (assessment._id) {
        setAssessmentInsights(prev => ({
          ...prev,
          [assessment._id]: {
            industryData: mockIndustryData,
            industryComparisons: mockComparisons,
            aiAnalysis: aiData,
            detailedInsights: insightsData
          }
        }));
      }
      
      // Mark insights as generated
      setInsightsGenerated(true);
      setAiLoading(false);
    } catch (err) {
      console.error('Error generating insights:', err);
      setError('Failed to generate insights. Please try again later.');
      setAiLoading(false);
    }
  };
  
  // Calculate average score from assessments
  const calculateAverageScore = (assessments) => {
    if (!assessments || assessments.length === 0) return 0;
    
    const totalScore = assessments.reduce((sum, assessment) => {
      return sum + (assessment.score || 0);
    }, 0);
    
    return Math.round((totalScore / assessments.length) * 100);
  };
  
  // Mock function to generate industry data (would be replaced with real API data)
  const generateMockIndustryData = (industryType) => {
    return {
      trends: [
        `Increasing reliance on technology for business continuity and crisis management.`,
        `Growing emphasis on sustainability and environmental resilience.`,
        `Rise in cyber threats necessitating enhanced cybersecurity measures.`,
        `Shift towards remote work and flexible business models due to global disruptions.`,
        `Focus on employee well-being and mental health as part of resilience strategies.`
      ],
      keyRisks: [
        `Regulatory compliance challenges specific to ${industryType}`,
        `Cyber threats targeting ${industryType} organizations`,
        `Supply chain disruptions affecting ${industryType}`,
        `Workforce challenges in ${industryType}`,
        `Reputational risks specific to ${industryType}`
      ]
    };
  };
  
  // Generate category comparisons based on the assessment's category scores
  const generateCategoryComparisons = (industryType, assessment) => {
    // Default industry averages (would be replaced with real data from API)
    const industryAverages = {
      'Governance & Strategy': 55,
      'Risk Management & BIA': 55,
      'Business Continuity Planning': 55,
      'IT & Cyber Resilience': 55,
      'Physical Security & Facilities': 55,
      'Supply Chain & Logistics': 55,
      'Human Resources & Workforce': 55,
      'Training & Awareness': 55,
      'Incident Response & Crisis Management': 55,
      'Testing & Maintenance': 55,
      'Compliance & Audit': 55,
      'Continuous Improvement & Metrics': 55,
      'Sustainability & ESG': 55,
      'Third-Party & Vendor': 55,
      'Data Management & Privacy': 55
    };
    
    try {
      // Try to extract category scores from the assessment
      let categoryScores = {};
      
      // Try different approaches to extract category scores from the assessment
      if (assessment.categoryScores) {
        // If assessment has categoryScores property
        categoryScores = assessment.categoryScores;
      } else if (assessment.categories) {
        // If assessment has categories array with scores
        assessment.categories.forEach(category => {
          if (category.name && category.score !== undefined) {
            categoryScores[category.name] = category.score;
          }
        });
      } else if (assessment.sections) {
        // If assessment has sections with scores
        assessment.sections.forEach(section => {
          if (section.name && section.score !== undefined) {
            categoryScores[section.name] = section.score;
          }
        });
      } else if (assessment.results && assessment.results.categories) {
        // If assessment has results with categories
        assessment.results.categories.forEach(category => {
          if (category.name && category.score !== undefined) {
            categoryScores[category.name] = category.score;
          }
        });
      }
      
      // If no category scores found, use the correct scores from the Maturity Scorecard (image 2)
      if (Object.keys(categoryScores).length === 0) {
        // Correct category scores based on the Maturity Scorecard
        categoryScores = {
          'Governance & Strategy': 53,
          'Risk Management & BIA': 57,
          'Business Continuity Planning': 43,
          'IT & Cyber Resilience': 43,
          'Physical Security & Facilities': 62,
          'Supply Chain & Logistics': 53,
          'Human Resources & Workforce': 45,
          'Training & Awareness': 67,
          'Incident Response & Crisis Management': 50,
          'Testing & Maintenance': 58,
          'Compliance & Audit': 58,
          'Continuous Improvement & Metrics': 42,
          'Sustainability & ESG': 60,
          'Third-Party & Vendor': 67,
          'Data Management & Privacy': 58
        };
      }
      
      // Generate comparisons for each category
      const comparisons = [];
      
      Object.entries(categoryScores).forEach(([category, score]) => {
        // Normalize score if it's a decimal (0-1 range)
        const normalizedScore = typeof score === 'number' ? 
          (score < 1 ? Math.round(score * 100) : Math.round(score)) : 0;
        
        // Get industry average for this category or use a default
        const industryAverage = industryAverages[category] || 55;
        
        // Calculate difference from industry average
        const difference = normalizedScore - industryAverage;
        
        // Add to comparisons array
        comparisons.push({
          category,
          score: normalizedScore,
          industryAverage,
          difference,
          isStrength: difference >= 0
        });
      });
      
      return comparisons;
    } catch (err) {
      console.error('Error generating category comparisons:', err);
      return [];
    }
  };
  
  // Get maturity level based on score
  const getMaturityLevel = (score) => {
    // Use the normalizeScore helper function to ensure score is properly normalized
    const normalizedScore = normalizeScore(score);
    
    // Find the appropriate maturity level
    if (normalizedScore >= 0 && normalizedScore < 1) {
      return maturityLevels[0]; // 'None' level
    }
    
    return maturityLevels.find(level => 
      normalizedScore >= level.range[0] && normalizedScore < level.range[1]
    ) || maturityLevels[0];
  };
  
  // Generate detailed AI insights based on maturity level, industry type, score and assessment data
  const generateDetailedInsights = (maturityLevel, industryType, score, assessment = null) => {
    // Normalize score using the helper function - default to 54% if no score
    const normalizedScore = score !== undefined ? normalizeScore(score) : 54;
    
    // Get the actual maturity level based on the score
    const actualMaturityLevel = getMaturityLevel(normalizedScore);
    
    // Calculate category scores based on the assessment data
    // For real assessments, we would use actual category scores
    // For mock data, we ensure scores are realistic
    let categoryScores = {};
    
    // Check if we have real category scores from the assessment
    if (assessment && assessment.categoryScores) {
      // Use real category scores from the assessment
      categoryScores = assessment.categoryScores;
    } else if (assessment && assessment.categories) {
      // Extract scores from categories array
      assessment.categories.forEach(category => {
        if (category.name && category.score !== undefined) {
          categoryScores[category.name] = category.score;
        }
      });
    } else {
      // If no real data, use mock data with realistic variation
      // These mock scores will never be negative
      categoryScores = {
        'Identification of Important Business Services': normalizedScore,
        'Governance & Accountability': Math.max(normalizedScore - 10, 0),
        'Setting Impact Tolerances': normalizedScore,
        'Mapping of Resources & Dependencies': normalizedScore,
        'Scenario Testing & Simulations': normalizedScore,
        'Communication & Continuous Improvement': Math.max(normalizedScore - 7, 0)
      };
    }
    
    // Normalize all scores to ensure they're in percentage format
    Object.keys(categoryScores).forEach(key => {
      if (categoryScores[key] < 1) {
        categoryScores[key] = Math.round(categoryScores[key] * 100);
      } else {
        categoryScores[key] = Math.round(categoryScores[key]);
      }
    });
    
    // This would be replaced with real AI-generated insights in production
    const insights = {
      analysis: `The organization's resilience posture is at ${normalizedScore}% maturity level alongside category scores that vary across crucial areas such as Identification of Important Business Services (${categoryScores['Identification of Important Business Services']}%) and Governance & Accountability (${categoryScores['Governance & Accountability']}%). This indicates a significant gap in the foundational elements necessary for a robust resilience framework. The absence of these elements suggests that the organization has not yet prioritized essential risk management practices or established effective governance structures to support resilience. Immediate action is required to address these vulnerabilities to protect against operational disruptions and regulatory scrutiny.`,
      
      detailedAnalysis: [
        `Your organization has made progress in establishing foundational resilience capabilities, particularly in documentation and awareness.`,
        `There are opportunities to enhance your resilience program through more consistent implementation and testing.`,
        `Your ${industryType} organization compares favorably to peers in some areas, but lags in others.`,
        `Focus on standardizing practices across departments and ensuring regular testing of recovery procedures.`
      ],
      
      trends: [
        `Increasing regulatory focus on operational resilience in ${industryType} organizations`,
        `Growing adoption of cloud-based backup solutions in the ${industryType} sector`,
        `Rising importance of cyber resilience frameworks for ${industryType} organizations`,
        `Shift toward integrated risk management approaches in ${industryType}`,
        `Emphasis on supply chain transparency and resilience in ${industryType}`
      ],
      
      keyRisks: [
        `Regulatory compliance challenges specific to ${industryType}`,
        `Cyber threats targeting ${industryType} organizations`,
        `Supply chain disruptions affecting ${industryType}`,
        `Workforce challenges in ${industryType}`,
        `Reputational risks specific to ${industryType}`
      ],
      
      improvementAreas: [
        `Standardize documentation across departments`,
        `Implement regular testing of recovery procedures`,
        `Develop metrics to measure resilience effectiveness`,
        `Enhance cross-functional collaboration`
      ],
      
      implementationSteps: [
        {
          step: "Create a resilience steering committee",
          timeframe: "1-2 months",
          resources: "Executive sponsorship, department representatives"
        },
        {
          step: "Standardize documentation templates",
          timeframe: "2-3 months",
          resources: "Document management system, template designer"
        },
        {
          step: "Implement a regular testing schedule",
          timeframe: "3-6 months",
          resources: "Testing coordinator, department participation"
        },
        {
          step: "Develop training programs for key personnel",
          timeframe: "2-4 months",
          resources: "Training materials, instructor time"
        },
        {
          step: "Establish performance metrics for the program",
          timeframe: "1-2 months",
          resources: "Analytics capability, reporting tools"
        },
        {
          step: "Integrate resilience considerations into change management",
          timeframe: "3-4 months",
          resources: "Change management process, resilience criteria"
        }
      ],
      
      itemsToConsider: [
        "Create a resilience steering committee",
        "Develop training programs for key personnel",
        "Implement a document management system for plans",
        "Establish regular review cycles for procedures",
        "Develop communication protocols for incidents"
      ],
      
      regulatoryConsiderations: `${industryType} organizations at this maturity level should focus on mapping specific regulatory requirements to documented processes. Consider developing a compliance matrix to track requirements and evidence of compliance.`
    };
    
    return insights;
  };
  

  
  // Generate detailed content for implementation steps
  const generateStepDetails = (step) => {
    // Create a unique identifier for the step based on content and category
    const stepId = `${step.category || 'generic'}-${step.step}`;
    
    // Use the step's category to customize the content
    const timeframeText = step.category === 'shortTerm' ? 'Short Term (0-3 months)' : 
                          step.category === 'mediumTerm' ? 'Medium Term (3-12 months)' : 
                          'Long Term (12+ months)';
    
    // Define step-specific implementation steps based on common resilience themes
    const implementationStepsMap = {
      // Short Term Steps
      'Conduct a thorough risk assessment to identify vulnerabilities and threats': [
        'Form a cross-functional risk assessment team with representatives from key departments',
        'Develop a comprehensive risk assessment methodology and evaluation criteria',
        'Conduct interviews and workshops with stakeholders to identify potential threats',
        'Analyze and prioritize identified risks based on likelihood and potential impact',
        'Document findings and develop initial recommendations for addressing critical vulnerabilities'
      ],
      'Develop governance and strategic frameworks that align with organizational goals': [
        'Review existing governance structures and identify gaps in resilience oversight',
        'Define roles and responsibilities for resilience management across the organization',
        'Develop a resilience policy that aligns with organizational mission and values',
        'Create a strategic framework that integrates resilience into business objectives',
        'Establish reporting mechanisms to track progress and maintain accountability'
      ],
      'Establish a crisis communication plan and notification procedures': [
        'Identify key stakeholders who need to be included in crisis communications',
        'Develop communication templates for various types of incidents and crises',
        'Establish notification procedures and escalation protocols',
        'Create a crisis communication team with clearly defined roles',
        'Test notification systems and conduct initial communication drills'
      ],
      'Implement basic business continuity measures for critical functions': [
        'Identify mission-critical business functions and their dependencies',
        'Conduct a business impact analysis for essential operations',
        'Develop basic continuity procedures for the most critical functions',
        'Identify and secure necessary resources for continuity operations',
        'Train key personnel on continuity procedures and responsibilities'
      ],
      'Create a resilience awareness program for key stakeholders': [
        'Develop educational materials about organizational resilience principles',
        'Conduct awareness sessions for leadership and key stakeholders',
        'Create a communication plan to regularly share resilience information',
        'Develop basic training modules for different organizational roles',
        'Establish feedback mechanisms to improve awareness initiatives'
      ],
      
      // Medium Term Steps
      'Develop comprehensive business continuity plans for all departments': [
        'Expand business impact analysis to include all organizational functions',
        'Create detailed recovery procedures for each department',
        'Develop resource requirements and allocation plans for continuity operations',
        'Establish recovery time objectives and recovery point objectives',
        'Document comprehensive continuity plans with clear activation triggers'
      ],
      'Implement regular testing and exercises of response procedures': [
        'Develop an exercise program with various scenarios and complexity levels',
        'Schedule and conduct tabletop exercises for response teams',
        'Implement functional drills to test specific capabilities',
        'Conduct full-scale exercises involving multiple departments',
        'Document lessons learned and update procedures based on exercise results'
      ],
      'Establish metrics and reporting for resilience capabilities': [
        'Define key performance indicators for resilience capabilities',
        'Develop a measurement framework for tracking resilience maturity',
        'Implement regular reporting mechanisms for leadership',
        'Create dashboards to visualize resilience metrics and trends',
        'Establish review processes to evaluate and improve based on metrics'
      ],
      'Integrate resilience considerations into procurement and vendor management': [
        'Review and update procurement policies to include resilience requirements',
        'Develop vendor assessment criteria for resilience capabilities',
        'Create contract templates with appropriate resilience clauses',
        'Implement vendor monitoring processes for critical suppliers',
        'Establish collaborative resilience planning with key vendors'
      ],
      'Develop cross-functional resilience teams and define responsibilities': [
        'Identify key personnel from each department for resilience teams',
        'Define clear roles and responsibilities for team members',
        'Develop coordination mechanisms between different teams',
        'Create training programs for team members on resilience principles',
        'Establish regular meeting cadence and reporting structure'
      ],
      
      // Long Term Steps
      'Integrate resilience into strategic planning and decision-making': [
        'Develop methodologies to incorporate resilience into strategic planning processes',
        'Create decision-making frameworks that include resilience considerations',
        'Implement resilience impact assessments for major organizational initiatives',
        'Establish resilience as a core value in organizational culture',
        'Develop long-term resilience roadmaps aligned with business strategy'
      ],
      'Establish a continuous improvement program for resilience capabilities': [
        'Implement regular capability assessments and maturity evaluations',
        'Develop mechanisms to capture and implement lessons learned',
        'Create innovation processes for enhancing resilience approaches',
        'Establish benchmarking against industry best practices',
        'Develop a formal management of change process for resilience programs'
      ],
      'Develop advanced scenario planning and horizon scanning capabilities': [
        'Establish systematic processes for identifying emerging risks',
        'Develop advanced scenario planning methodologies',
        'Create cross-functional teams for horizon scanning activities',
        'Implement regular scenario exercises with leadership',
        'Integrate findings into strategic planning and risk management'
      ],
      'Create resilience innovation programs to address emerging risks': [
        'Establish innovation teams focused on resilience challenges',
        'Develop processes for testing and piloting new resilience approaches',
        'Create funding mechanisms for resilience innovation initiatives',
        'Implement knowledge sharing platforms for resilience innovations',
        'Establish partnerships with research institutions and industry groups'
      ],
      'Build resilience partnerships with external stakeholders and industry groups': [
        'Identify key external stakeholders for resilience collaboration',
        'Develop formal partnership agreements with critical external entities',
        'Create information sharing mechanisms with industry peers',
        'Participate in industry resilience initiatives and working groups',
        'Establish community engagement programs for broader resilience'
      ]
    };
    
    // Generate implementation steps based on the step title or use default steps
    const uniqueImplementationSteps = implementationStepsMap[step.step] || 
      (step.category === 'shortTerm' ? [
        'Identify key stakeholders and form a cross-functional team',
        'Conduct a rapid assessment of current capabilities and gaps',
        'Develop an implementation plan with quick wins identified',
        'Secure necessary resources and executive sponsorship',
        'Implement initial phase with regular progress reviews'
      ] : step.category === 'mediumTerm' ? [
        'Build upon short-term actions and expand implementation scope',
        'Develop comprehensive documentation and procedures',
        'Integrate with existing organizational systems and processes',
        'Conduct training and awareness programs for all stakeholders',
        'Establish metrics and reporting mechanisms for tracking progress'
      ] : [
        'Develop a strategic roadmap for full enterprise implementation',
        'Integrate capabilities into organizational culture and values',
        'Establish governance structures for ongoing oversight',
        'Implement continuous improvement and feedback mechanisms',
        'Develop industry-leading capabilities and best practices'
      ]);
    
    // Define step-specific challenges
    const challengesMap = {
      // Short term challenges for specific steps
      'Conduct a thorough risk assessment to identify vulnerabilities and threats': [
        'Gathering accurate data from across the organization',
        'Ensuring stakeholder participation and honest feedback',
        'Balancing depth of analysis with time constraints',
        'Addressing potential resistance to identifying vulnerabilities',
        'Prioritizing findings when many vulnerabilities are identified'
      ],
      // Add more specific challenges for other steps as needed
    };
    
    // Generate challenges based on the step title or use default challenges
    const uniqueChallenges = challengesMap[step.step] || 
      (step.category === 'shortTerm' ? [
        'Securing immediate resources amid competing organizational priorities',
        'Building initial momentum and stakeholder buy-in across departments',
        'Balancing quick wins with longer-term strategic needs',
        'Coordinating efforts across departments on short notice',
        'Measuring early impact to justify continued investment'
      ] : step.category === 'mediumTerm' ? [
        'Maintaining momentum after initial implementation phases',
        'Securing ongoing budget and resource commitments',
        'Managing scope expansion as the initiative grows',
        'Addressing resistance to process and procedural changes',
        'Demonstrating continued value to executive stakeholders'
      ] : [
        'Sustaining long-term commitment from leadership team',
        'Adapting to changing business and risk landscapes',
        'Preventing initiative fatigue over extended implementation timeframes',
        'Measuring return on investment for strategic initiatives',
        'Maintaining alignment with evolving business objectives'
      ]);
    
    // Define step-specific benefits
    const benefitsMap = {
      // Short term benefits for specific steps
      'Conduct a thorough risk assessment to identify vulnerabilities and threats': [
        'Clear understanding of organizational vulnerabilities and threats',
        'Data-driven prioritization of resilience investments',
        'Early identification of previously unknown risks',
        'Increased awareness of resilience issues across the organization',
        'Foundation for developing targeted mitigation strategies'
      ],
      // Add more specific benefits for other steps as needed
    };
    
    // Generate benefits based on the step title or use default benefits
    const uniqueBenefits = benefitsMap[step.step] || 
      (step.category === 'shortTerm' ? [
        'Rapid improvements in critical resilience capabilities',
        'Visible demonstration of organizational commitment to resilience',
        'Early identification of major gaps and vulnerabilities',
        'Improved readiness for immediate threats and disruptions',
        'Foundation established for more comprehensive improvements'
      ] : step.category === 'mediumTerm' ? [
        'Systematic improvement in organizational resilience capabilities',
        'Enhanced coordination and alignment across business functions',
        'More robust response capabilities for a wider range of scenarios',
        'Improved stakeholder and customer confidence in resilience',
        'Better alignment with industry standards and regulatory requirements'
      ] : [
        'Strategic competitive advantage through superior resilience capabilities',
        'Industry-leading practices in key resilience areas',
        'Resilience fully integrated into organizational culture and decision-making',
        'Adaptive capacity for emerging and previously unknown threats',
        'Sustainable resilience program with continuous improvement mechanisms'
      ]);
                        
    // Map of detailed content for each implementation step
    const stepDetailsMap = {
      'Create a resilience steering committee': {
        description: 'A resilience steering committee provides governance and oversight for your organization\'s resilience initiatives, ensuring alignment with strategic objectives.',
        timeframe: '1-2 months',
        resources: 'Executive sponsorship, department representatives',
        implementation: [
          'Identify key stakeholders from across the organization',
          'Define the committee charter and responsibilities',
          'Establish regular meeting cadence and reporting structure',
          'Develop decision-making frameworks and escalation paths',
          'Create communication channels to disseminate information'
        ],
        potentialChallenges: [
          'Securing executive buy-in and ongoing commitment',
          'Balancing representation across departments',
          'Maintaining momentum and engagement over time',
          'Avoiding "committee paralysis" in decision-making',
          'Measuring the committee\'s effectiveness'
        ],
        keyBenefits: [
          'Improved governance of resilience initiatives',
          'Enhanced cross-functional collaboration',
          'Consistent prioritization of resilience investments',
          'Better alignment between resilience and business objectives',
          'Increased visibility of resilience efforts to leadership'
        ]
      },
      'Standardize documentation templates': {
        description: 'Standardized documentation templates ensure consistency across resilience plans and procedures, making them easier to maintain and implement.',
        timeframe: '2-3 months',
        resources: 'Document management system, template designer',
        implementation: [
          'Inventory existing documentation and identify gaps',
          'Develop template standards and approval processes',
          'Create templates for different document types',
          'Train document owners on template usage',
          'Implement version control and review cycles'
        ],
        potentialChallenges: [
          'Resistance to changing existing documentation',
          'Balancing standardization with flexibility for different needs',
          'Managing the transition to new templates',
          'Ensuring templates remain relevant over time',
          'Measuring compliance with template standards'
        ],
        keyBenefits: [
          'Improved consistency across resilience documentation',
          'Reduced time to create and update plans',
          'Enhanced readability and usability during incidents',
          'Better knowledge transfer when personnel changes occur',
          'Easier compliance verification during audits'
        ]
      },
      'Implement a regular testing schedule': {
        description: 'Regular testing of resilience plans validates their effectiveness and identifies areas for improvement before a real incident occurs.',
        timeframe: '3-6 months',
        resources: 'Testing coordinator, department participation',
        implementation: [
          'Develop a testing strategy and annual schedule',
          'Create test scenarios based on risk assessments',
          'Train facilitators and participants',
          'Conduct tests with increasing complexity',
          'Document lessons learned and implement improvements'
        ],
        potentialChallenges: [
          'Securing time commitments from busy departments',
          'Creating realistic scenarios without disrupting operations',
          'Balancing test scope with available resources',
          'Preventing "check-the-box" exercises without real value',
          'Tracking and implementing improvements after tests'
        ],
        keyBenefits: [
          'Validated plans that work during real incidents',
          'Improved staff familiarity with response procedures',
          'Identified gaps before they impact real recovery efforts',
          'Enhanced confidence in resilience capabilities',
          'Demonstrated due diligence to stakeholders and regulators'
        ]
      },
      'Develop training programs for key personnel': {
        description: 'Training programs ensure that personnel have the knowledge and skills needed to implement resilience plans effectively during incidents.',
        timeframe: '2-4 months',
        resources: 'Training materials, instructor time',
        implementation: [
          'Identify training needs and target audiences',
          'Develop curriculum and delivery methods',
          'Create training materials and exercises',
          'Implement training schedule and tracking',
          'Evaluate effectiveness and refine program'
        ],
        potentialChallenges: [
          'Competing priorities for staff time',
          'Developing engaging and relevant content',
          'Maintaining training currency as plans change',
          'Measuring training effectiveness',
          'Securing budget for ongoing training needs'
        ],
        keyBenefits: [
          'Improved response capabilities during incidents',
          'Reduced dependency on specific individuals',
          'Enhanced confidence among response personnel',
          'Faster and more effective incident resolution',
          'Demonstrated commitment to preparedness'
        ]
      },
      'Establish performance metrics for the program': {
        description: 'Performance metrics provide objective measures of your resilience program\'s effectiveness and help identify areas for improvement.',
        timeframe: '1-2 months',
        resources: 'Analytics capability, reporting tools',
        implementation: [
          'Define key performance indicators aligned with objectives',
          'Establish measurement methodologies and data sources',
          'Implement reporting processes and dashboards',
          'Set baseline measurements and improvement targets',
          'Review and refine metrics periodically'
        ],
        potentialChallenges: [
          'Identifying meaningful metrics beyond compliance',
          'Collecting data without excessive administrative burden',
          'Avoiding metrics that drive wrong behaviors',
          'Balancing leading and lagging indicators',
          'Maintaining focus on improvement rather than just reporting'
        ],
        keyBenefits: [
          'Objective assessment of program effectiveness',
          'Early identification of program gaps',
          'Data-driven decision making for investments',
          'Demonstrated value to leadership and stakeholders',
          'Continuous improvement based on performance trends'
        ]
      },
      'Integrate resilience considerations into change management': {
        description: 'Integrating resilience into change management ensures that new initiatives and systems are designed with resilience in mind from the start.',
        timeframe: '3-4 months',
        resources: 'Change management process, resilience criteria',
        implementation: [
          'Assess current change management processes',
          'Develop resilience criteria for different change types',
          'Update change management procedures and forms',
          'Train change managers on resilience considerations',
          'Implement review and approval gates'
        ],
        potentialChallenges: [
          'Resistance to adding steps to change processes',
          'Balancing resilience with other project constraints',
          'Ensuring meaningful assessment rather than checkbox compliance',
          'Measuring the impact on overall resilience',
          'Maintaining engagement when urgent changes are needed'
        ],
        keyBenefits: [
          'Reduced introduction of new vulnerabilities',
          'Lower remediation costs through "resilience by design"',
          'Improved awareness of resilience across the organization',
          'Better alignment between projects and resilience objectives',
          'Enhanced overall organizational resilience posture'
        ]
      },
      'Conduct a thorough risk assessment': {
        description: 'A thorough risk assessment identifies vulnerabilities and threats to your organization, providing the foundation for targeted resilience improvements.',
        timeframe: '2-3 months',
        resources: 'Risk assessment methodology, cross-functional team',
        implementation: [
          'Define assessment scope and methodology',
          'Identify and categorize critical assets and processes',
          'Assess threats, vulnerabilities, and potential impacts',
          'Evaluate existing controls and their effectiveness',
          'Prioritize risks based on likelihood and impact'
        ],
        potentialChallenges: [
          'Gathering accurate information across departments',
          'Quantifying risks with limited historical data',
          'Avoiding subjective bias in assessments',
          'Managing the scope to maintain focus',
          'Translating findings into actionable improvements'
        ],
        keyBenefits: [
          'Targeted investments in highest-risk areas',
          'Improved understanding of organizational vulnerabilities',
          'Data-driven prioritization of resilience initiatives',
          'Enhanced awareness of risks across the organization',
          'Documented due diligence for stakeholders and regulators'
        ]
      }
    };
    
    // Get the step title to look up in the map
    const stepTitle = step.step || step;
    
    // Return the detailed content for this step, or generate generic content if not found
    return stepDetailsMap[stepTitle] || {
      title: stepTitle,
      description: `Implementing "${stepTitle}" is a critical component of improving your organization's resilience maturity.`,
      timeframe: step.timeframe || timeframeText,
      resources: step.resources || 'Varies',
      implementation: uniqueImplementationSteps,
      potentialChallenges: uniqueChallenges,
      keyBenefits: uniqueBenefits
    };
  };

  // Generate detailed content for items to consider
  const generateItemDetails = (item) => {
    // Map of detailed content for each item to consider
    const itemDetailsMap = {
      'Create a resilience steering committee': {
        description: 'A resilience steering committee provides governance and oversight for resilience initiatives across the organization, ensuring alignment with strategic objectives.',
        implementation: [
          'Identify key stakeholders from different departments',
          'Define the committee charter and responsibilities',
          'Establish regular meeting cadence and reporting structure',
          'Develop decision-making frameworks and escalation paths',
          'Create communication channels to disseminate information'
        ],
        benefits: [
          'Improved governance of resilience initiatives',
          'Enhanced cross-functional collaboration',
          'Consistent prioritization of resilience investments',
          'Better alignment between resilience and business objectives',
          'Increased visibility of resilience efforts to leadership'
        ],
        potentialChallenges: [
          'Securing consistent participation from busy executives and department heads',
          'Balancing strategic oversight with tactical implementation details',
          'Maintaining momentum and engagement between meetings',
          'Measuring and demonstrating the committee\'s impact on resilience outcomes',
          'Preventing the committee from becoming a bureaucratic bottleneck'
        ]
      },
      'Develop training programs for key personnel': {
        description: 'Comprehensive training programs ensure that personnel have the knowledge and skills needed to implement resilience plans effectively during incidents.',
        implementation: [
          'Identify training needs and target audiences',
          'Develop curriculum and delivery methods',
          'Create training materials and exercises',
          'Implement training schedule and tracking',
          'Evaluate effectiveness and refine program'
        ],
        benefits: [
          'Improved response capabilities during incidents',
          'Reduced dependency on specific individuals',
          'Enhanced confidence among response personnel',
          'Faster and more effective incident resolution',
          'Demonstrated commitment to preparedness'
        ],
        potentialChallenges: [
          'Securing adequate time for training in busy schedules',
          'Measuring training effectiveness and return on investment',
          'Keeping training content current with evolving threats',
          'Addressing varying learning styles and technical capabilities',
          'Maintaining engagement and preventing training fatigue'
        ]
      },
      'Implement a document management system for plans': {
        description: 'A document management system ensures that resilience plans are accessible, current, and properly controlled throughout their lifecycle.',
        implementation: [
          'Evaluate document management system options',
          'Establish document taxonomy and metadata requirements',
          'Define version control and approval workflows',
          'Migrate existing documents to the new system',
          'Train users on system functionality and procedures'
        ],
        benefits: [
          'Improved accessibility to current plan versions',
          'Enhanced version control and document history',
          'Streamlined review and approval processes',
          'Better integration between related documents',
          'Reduced risk of using outdated procedures during incidents'
        ],
        potentialChallenges: [
          'Overcoming resistance to new systems and processes',
          'Balancing security requirements with ease of access',
          'Managing the cost and complexity of implementation',
          'Ensuring consistent adoption across the organization',
          'Integrating with existing IT infrastructure and systems'
        ]
      },
      'Establish regular review cycles for procedures': {
        description: 'Regular review cycles ensure that resilience procedures remain current, effective, and aligned with organizational changes and emerging threats.',
        implementation: [
          'Define review frequency based on document criticality',
          'Establish review teams and responsibilities',
          'Develop review criteria and checklists',
          'Implement tracking and reminder systems',
          'Create feedback loops for continuous improvement'
        ],
        benefits: [
          'Improved currency and relevance of procedures',
          'Enhanced alignment with organizational changes',
          'Reduced risk of procedure failures during incidents',
          'Increased stakeholder confidence in plans',
          'Better compliance with regulatory requirements'
        ],
        potentialChallenges: [
          'Maintaining review discipline amid competing priorities',
          'Preventing reviews from becoming superficial checkbox exercises',
          'Securing participation from subject matter experts with limited availability',
          'Tracking and implementing changes identified during reviews',
          'Measuring the effectiveness of the review process itself'
        ]
      },
      'Develop communication protocols for incidents': {
        description: 'Clear communication protocols ensure that the right information reaches the right stakeholders at the right time during incidents, improving coordination and response.',
        implementation: [
          'Identify stakeholder groups and communication needs',
          'Develop message templates for different incident types',
          'Establish communication channels and backup methods',
          'Define escalation paths and approval requirements',
          'Test protocols through simulation exercises'
        ],
        benefits: [
          'Improved coordination during incidents',
          'Reduced confusion and misinformation',
          'Enhanced stakeholder confidence',
          'More efficient resource allocation',
          'Better compliance with notification requirements'
        ],
        potentialChallenges: [
          'Ensuring protocols remain effective when communication infrastructure is compromised',
          'Balancing speed with accuracy in crisis communications',
          'Preventing message fatigue during prolonged incidents',
          'Adapting to different stakeholder communication preferences and needs',
          'Managing sensitive information while maintaining transparency'
        ]
      },
      'Engage with external experts or consultants': {
        description: 'External experts and consultants provide specialized knowledge, objective assessments, and industry insights that can accelerate your resilience program development.',
        implementation: [
          'Identify specific areas requiring external expertise',
          'Develop selection criteria and evaluation process',
          'Create clear scope and deliverables for engagements',
          'Establish knowledge transfer requirements',
          'Implement mechanisms to sustain improvements after engagement ends'
        ],
        benefits: [
          'Access to specialized expertise and industry best practices',
          'Objective assessment of current capabilities',
          'Accelerated program development',
          'Fresh perspectives on existing challenges',
          'Credibility with leadership and stakeholders'
        ],
        potentialChallenges: [
          'Justifying the cost and demonstrating return on investment',
          'Ensuring knowledge transfer for sustainable improvements',
          'Selecting experts with relevant industry experience',
          'Managing scope creep and project timelines',
          'Integrating external recommendations with internal constraints'
        ]
      },
      'Foster a culture of resilience': {
        description: 'A resilience culture embeds preparedness and adaptability into the organization\'s values and behaviors, making resilience everyone\'s responsibility rather than just a program.',
        implementation: [
          'Assess current organizational culture and resilience awareness',
          'Develop resilience principles and behaviors',
          'Create awareness and education programs',
          'Implement recognition for resilience contributions',
          'Integrate resilience into performance expectations'
        ],
        benefits: [
          'Improved resilience awareness across all levels',
          'Enhanced proactive identification of risks',
          'Increased staff engagement in resilience activities',
          'More effective response during incidents',
          'Sustained resilience improvements over time'
        ],
        potentialChallenges: [
          'Overcoming resistance to change and organizational inertia',
          'Measuring cultural changes and their impact on resilience outcomes',
          'Maintaining momentum beyond initial awareness campaigns',
          'Addressing varying levels of engagement across departments',
          'Balancing resilience focus with other organizational priorities'
        ]
      },
      'Regularly review and update risk management frameworks': {
        description: 'Regular reviews of risk management frameworks ensure they remain effective against evolving threats and aligned with organizational changes.',
        implementation: [
          'Establish a review schedule for risk frameworks',
          'Monitor emerging threats and industry developments',
          'Assess framework effectiveness against actual incidents',
          'Update risk assessment methodologies',
          'Align risk frameworks with organizational changes'
        ],
        benefits: [
          'Improved identification of emerging risks',
          'Enhanced alignment with organizational priorities',
          'More effective risk mitigation strategies',
          'Better resource allocation for risk management',
          'Increased confidence in risk assessment results'
        ],
        potentialChallenges: [
          'Keeping frameworks current with rapidly evolving threat landscapes',
          'Avoiding framework complexity that inhibits practical application',
          'Balancing qualitative and quantitative risk assessment approaches',
          'Securing consistent executive support for framework updates',
          'Measuring the effectiveness of risk management frameworks'
        ]
      },
      'Explore partnerships with industry organizations': {
        description: 'Industry partnerships provide access to shared learning, best practices, and collaborative approaches to common resilience challenges.',
        implementation: [
          'Identify relevant industry associations and working groups',
          'Evaluate partnership opportunities and benefits',
          'Allocate resources for active participation',
          'Establish internal processes to implement learnings',
          'Measure and report on partnership value'
        ],
        benefits: [
          'Access to industry best practices and standards',
          'Shared learning from others\'s experiences',
          'Collaborative approach to common challenges',
          'Early awareness of industry developments',
          'Networking opportunities with peers'
        ],
        potentialChallenges: [
          'Balancing information sharing with competitive and security concerns',
          'Justifying time and resource commitments for participation',
          'Translating industry insights into actionable improvements',
          'Maintaining consistent engagement over time',
          'Measuring the tangible benefits of partnership activities'
        ]
      }
    };
    
    // Return the detailed content for this item, or generate generic content if not found
    return itemDetailsMap[item] || {
      title: item,
      description: `"${item}" is an important consideration for organizations seeking to enhance their resilience capabilities.`,
      implementation: [
        'Establish a working group to explore this consideration',
        'Research industry best practices and standards',
        'Develop a business case for implementation',
        'Create a phased approach for adoption',
        'Establish metrics to measure effectiveness'
      ],
      benefits: [
        'Enhanced organizational resilience',
        'Improved stakeholder confidence',
        'Better alignment with industry standards',
        'Reduced risk exposure',
        'Improved recovery capabilities'
      ],
      potentialChallenges: [
        'Securing necessary budget and resources for implementation',
        'Gaining organizational buy-in across different departments',
        'Measuring the effectiveness of the implemented approach',
        'Maintaining momentum after initial implementation',
        'Adapting the approach to fit specific organizational needs'
      ]
    };
  };

  const handleSelectStep = (step, index, category) => {
    // Create a unique identifier for each step that includes its index and category
    const stepIdentifier = `${category}-${index}`;
    
    // Only update if this is a different step than currently selected
    if (!selectedStep || selectedStep.stepIdentifier !== stepIdentifier) {
      // Generate unique details for this specific step
      const details = generateStepDetails({
        ...step,
        category,
        index,
        stepIdentifier
      });
      
      // Set the selected step with additional unique identifier
      setSelectedStep({
        ...step,
        category,
        index,
        stepIdentifier
      });
      
      // Set the detail panel content
      setDetailPanelContent(details);
      setShowStepDetails(true);
      setShowItemDetails(false);
    }
  };

  const handleSelectItem = (item) => {
    const details = generateItemDetails(item);
    setSelectedItem(item);
    setDetailPanelContent(details);
    setShowItemDetails(true);
    setShowStepDetails(false);
  };
  
  const closeDetailPanel = () => {
    setShowStepDetails(false);
    setShowItemDetails(false);
    setSelectedStep(null);
    setSelectedItem(null);
    setDetailPanelContent(null);
  };
  
  // Function to export the industry analysis as a PDF report and save to database
  const handleExportReport = async () => {
    try {
      setExportLoading(true);
      
      // Collect all implementation step details
      const implementationStepDetails = [];
      if (detailedInsights && detailedInsights.implementationSteps) {
        // First, organize steps by timeframe
        const organizedSteps = {
          short: [],
          medium: [],
          long: []
        };
        
        // Process each step and categorize it
        for (const step of detailedInsights.implementationSteps) {
          const stepObj = typeof step === 'string' 
            ? { step, timeframe: 'Varies', resources: 'Varies' } 
            : step;
          
          // Determine the timeframe category
          const timeframe = stepObj.timeframe ? stepObj.timeframe.toLowerCase() : '';
          const category = stepObj.category || '';
          
          let timeframeCategory = 'short';
          if (category === 'shortTerm' || category === 'short' || timeframe.includes('short')) {
            timeframeCategory = 'short';
          } else if (category === 'mediumTerm' || category === 'medium' || timeframe.includes('medium')) {
            timeframeCategory = 'medium';
          } else if (category === 'longTerm' || category === 'long' || timeframe.includes('long')) {
            timeframeCategory = 'long';
          }
          
          // Generate step details
          const stepDetails = generateStepDetails(stepObj);
          const stepWithDetails = {
            step: stepObj.step,
            timeframe: stepObj.timeframe || (timeframeCategory === 'short' ? 'Short Term (0-3 months)' : 
                      timeframeCategory === 'medium' ? 'Medium Term (3-12 months)' : 
                      'Long Term (12+ months)'),
            resources: stepObj.resources || 'Varies',
            category: timeframeCategory,
            description: stepDetails.description,
            implementation: stepDetails.implementation,
            potentialChallenges: stepDetails.potentialChallenges,
            keyBenefits: stepDetails.keyBenefits
          };
          
          // Add to the appropriate category
          organizedSteps[timeframeCategory].push(stepWithDetails);
        }
        
        // Ensure each timeframe has exactly 5 steps
        ['short', 'medium', 'long'].forEach(timeframe => {
          // If we have more than 5 steps, keep only the first 5
          if (organizedSteps[timeframe].length > 5) {
            organizedSteps[timeframe] = organizedSteps[timeframe].slice(0, 5);
          }
        });
        
        // Combine all steps into a single array for the report
        implementationStepDetails.push(...organizedSteps.short);
        implementationStepDetails.push(...organizedSteps.medium);
        implementationStepDetails.push(...organizedSteps.long);
      }
      
      // Collect all item details
      const itemConsiderDetails = [];
      // Get items to consider from either aiAnalysis or detailedInsights
      const itemsToConsider = aiAnalysis.itemsToConsider || 
        (detailedInsights ? detailedInsights.itemsToConsider : []);
        
      if (itemsToConsider && itemsToConsider.length > 0) {
        // Process each item
        for (const item of itemsToConsider) {
          const itemDetails = generateItemDetails(item);
          // Add the item with its details
          itemConsiderDetails.push({
            item: item, // Keep the original item text
            title: item, // Add title property for the report generator
            description: itemDetails.description,
            implementation: itemDetails.implementation,
            benefits: itemDetails.benefits,
            examples: itemDetails.examples
          });
        }
      }
      
      // First save the report data to the database
      try {
        console.log('Saving report data to database...');
        const reportData = {
          assessmentId: selectedAssessment._id,
          organizationId: selectedOrganization._id,
          aiAnalysis,
          industryComparisons,
          industryData,
          detailedInsights,
          implementationStepDetails,
          itemConsiderDetails
        };
        
        const saveResponse = await axios.post('/api/industry-analysis-reports', reportData, {
          headers: { 'x-auth-token': token }
        });
        
        console.log('Report saved to database:', saveResponse.data);
      } catch (dbErr) {
        console.error('Error saving report to database:', dbErr);
        // Continue with PDF generation even if database save fails
      }
      
      // Generate the PDF report with all the data using the modernized report generator
      const success = await generateModernIndustryAnalysisReport({
        organization: selectedOrganization,
        assessment: selectedAssessment,
        industryData,
        industryComparisons,
        aiAnalysis,
        detailedInsights,
        implementationSteps: implementationStepDetails,
        itemsToConsider: itemConsiderDetails
      });
      
      if (success) {
        console.log('PDF report generated successfully');
        
        // Create a record in the main reports collection so it appears in the Reports page
        try {
          const reportTitle = `${selectedOrganization.name} - Industry Analysis Report`;
          
          // Get the current user ID from the token
          let userId = null;
          try {
            // Decode the JWT token to get the user ID
            const tokenParts = token.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              userId = payload.id || payload.userId;
              console.log('Extracted user ID from token:', userId);
            }
          } catch (tokenErr) {
            console.error('Error extracting user ID from token:', tokenErr);
          }
          
          await axios.post('/api/reports', {
            title: reportTitle,
            type: 'Industry Analysis',
            assessmentId: selectedAssessment._id,
            organizationId: selectedOrganization._id,
            userId: userId, // Include the user ID
            format: 'application/pdf',
            // Since we don't store the actual PDF on the server, we can't provide a fileUrl
            // But we can add a flag to indicate it's a client-side download
            clientSideDownload: true,
            generatedAt: new Date().toISOString()
          }, {
            headers: { 'x-auth-token': token }
          });
          console.log('Created record in reports collection for the Industry Analysis report');
        } catch (reportErr) {
          console.error('Error creating report record:', reportErr);
          // Continue even if this fails
        }
        
        // Show success message to user
        setAiError(null);
      } else {
        console.error('Failed to generate PDF report');
        setError('Failed to generate PDF report. Please try again later.');
      }
    } catch (err) {
      console.error('Error exporting report:', err);
      setError('Failed to export report. Please try again later.');
    } finally {
      setExportLoading(false);
    }
  };
  
  // Helper functions for the comparison bars
  const comparisonFill = (percentage) => ({
    width: `${percentage}%`,
    height: '100%',
    backgroundColor: '#D2BA7B',
    borderRadius: '12px'
  });
  
  const comparisonMarker = (percentage) => ({
    position: 'absolute',
    left: `${percentage}%`,
    top: '0',
    height: '24px',
    width: '2px',
    backgroundColor: '#243444'
  });
  
  const comparisonLabel = (percentage) => ({
    position: 'absolute',
    left: `${percentage}%`,
    top: '-25px',
    transform: 'translateX(-50%)',
    backgroundColor: '#243444',
    color: '#FFFAFA',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '0.8rem',
    whiteSpace: 'nowrap'
  });
  
  // Styles
  const styles = {
    container: {
      padding: '0 20px 20px',
      paddingTop: '65px', // Reduced space after header
      backgroundColor: 'var(--background-color)',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center' // Center content horizontally
    },
    selectorBar: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      padding: '10px 20px',
      backgroundColor: '#FFFAFA',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      marginBottom: '15px',
      width: '100%',
      maxWidth: '1200px' // Match main content width
    },
    selectorContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      color: '#243444' // Navy font color for selectors
    },
    dashboardContent: {
      marginBottom: '15px',
      width: '100%',
      maxWidth: '1200px'
    },
    section: {
      backgroundColor: '#FFFAFA',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '30px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    sectionTitle: {
      color: '#243444',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      marginBottom: '15px',
      fontSize: '1.5rem'
    },
    sectionIcon: {
      marginRight: '10px',
      color: '#D2BA7B'
    },
    card: {
      backgroundColor: '#FFFAFA',
      borderRadius: '8px',
      padding: '15px',
      marginBottom: '15px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '20px'
    },
    subsectionTitle: {
      color: '#243444',
      fontWeight: 'bold',
      marginBottom: '10px',
      fontSize: '1.2rem'
    },
    list: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    listItem: {
      padding: '8px 0',
      borderBottom: '1px solid #f0f0f0',
      display: 'flex',
      alignItems: 'flex-start'
    },
    listItemIcon: {
      color: '#D2BA7B',
      marginRight: '10px',
      marginTop: '4px'
    },
    detailPanel: {
      position: 'fixed',
      top: '80px',
      right: '0',
      width: '450px',
      height: 'calc(100vh - 80px)',
      backgroundColor: '#FFFAFA',
      boxShadow: '-2px 0 10px rgba(0,0,0,0.1)',
      zIndex: 1000,
      overflowY: 'auto',
      padding: '20px',
      transition: 'transform 0.3s ease',
      transform: 'translateX(0)',
      display: 'flex',
      flexDirection: 'column'
    },
    detailPanelHidden: {
      transform: 'translateX(100%)'
    },
    detailPanelHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      borderBottom: '2px solid #D2BA7B',
      paddingBottom: '10px'
    },
    detailPanelTitle: {
      color: '#243444',
      fontWeight: 'bold',
      fontSize: '1.3rem',
      margin: 0
    },
    closeButton: {
      background: 'none',
      border: 'none',
      color: '#4C5454',
      fontSize: '1.5rem',
      cursor: 'pointer',
      padding: '5px'
    },
    detailPanelSection: {
      marginBottom: '20px'
    },
    detailPanelSectionTitle: {
      color: '#243444',
      fontWeight: 'bold',
      fontSize: '1.1rem',
      marginBottom: '10px',
      display: 'flex',
      alignItems: 'center'
    },
    detailPanelSectionIcon: {
      marginRight: '8px',
      color: '#D2BA7B'
    },
    detailPanelDescription: {
      fontSize: '1rem',
      lineHeight: '1.5',
      color: '#4C5454',
      marginBottom: '15px'
    },
    detailPanelMetadata: {
      display: 'flex',
      justifyContent: 'space-between',
      backgroundColor: '#f5f5f5',
      padding: '10px 15px',
      borderRadius: '6px',
      marginBottom: '15px'
    },
    detailPanelMetadataItem: {
      display: 'flex',
      flexDirection: 'column'
    },
    detailPanelMetadataLabel: {
      fontSize: '0.8rem',
      color: '#4C5454',
      marginBottom: '5px'
    },
    detailPanelMetadataValue: {
      fontSize: '1rem',
      fontWeight: 'bold',
      color: '#243444'
    },
    maturityLevelIndicator: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '15px',
      marginBottom: '30px'
    },
    maturityLevelItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '16%'
    },
    maturityLevelDot: (color, isActive) => ({
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      backgroundColor: color,
      marginBottom: '5px',
      border: isActive ? '2px solid #243444' : 'none',
      boxShadow: isActive ? '0 0 5px rgba(0,0,0,0.3)' : 'none'
    }),
    maturityLevelLabel: {
      fontSize: '0.7rem',
      textAlign: 'center',
      color: '#4C5454'
    },
    maturityLevelRange: {
      fontSize: '0.6rem',
      color: '#4C5454'
    },
    button: {
      backgroundColor: '#243444',
      color: '#FFFAFA',
      border: 'none',
      borderRadius: '4px',
      padding: '10px 15px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      marginTop: '20px'
    },
    comparisonBar: {
      height: '24px',
      backgroundColor: '#f0f0f0',
      borderRadius: '12px',
      position: 'relative',
      marginTop: '10px',
      marginBottom: '20px',
      overflow: 'hidden' // Prevent gold bar from overflowing
    },
    categoryComparisonBar: {
      height: '20px',
      backgroundColor: '#f0f0f0',
      borderRadius: '10px',
      position: 'relative',
      marginTop: '10px',
      marginBottom: '5px',
      overflow: 'hidden'
    },
    averageBar: {
      height: '20px',
      backgroundColor: '#4C5454',
      borderRadius: '10px',
      position: 'absolute',
      top: 0,
      left: 0,
      zIndex: 1
    },
    assessmentBar: {
      height: '20px',
      backgroundColor: '#D2BA7B',
      borderRadius: '10px',
      position: 'absolute',
      top: 0,
      left: 0,
      zIndex: 2
    },
    regenerateButton: {
      backgroundColor: '#243444',
      color: '#FFFAFA',
      border: 'none',
      borderRadius: '4px',
      padding: '8px 15px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      marginBottom: '20px',
      fontSize: '0.9rem'
    },
    exportButton: {
      backgroundColor: '#D2BA7B',
      color: '#243444',
      border: 'none',
      borderRadius: '4px',
      padding: '8px 15px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      marginLeft: '10px',
      fontSize: '0.9rem'
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-start',
      marginBottom: '20px'
    },
    exportingOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    },
    exportingText: {
      color: '#FFFAFA',
      marginTop: '15px',
      fontSize: '1.1rem',
      fontWeight: 'bold'
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '50vh'
    },
    spinner: {
      fontSize: '40px',
      color: '#243444',
      marginBottom: '20px'
    },
    errorContainer: {
      padding: '20px',
      backgroundColor: '#f8d7da',
      color: '#721c24',
      borderRadius: '8px',
      margin: '20px'
    },
    analysisText: {
      lineHeight: '1.6',
      color: '#4C5454'
    },
    comparisonCard: {
      backgroundColor: '#FFFAFA',
      borderRadius: '8px',
      padding: '15px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    categoryTitle: {
      color: '#243444',
      fontWeight: 'bold',
      marginBottom: '10px'
    },
    implementationStep: {
      display: 'flex',
      flexDirection: 'column',
      padding: '15px',
      backgroundColor: '#f9f9f9',
      borderRadius: '8px',
      marginBottom: '15px',
      border: '1px solid #e0e0e0'
    },
    stepHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '10px'
    },
    stepTitle: {
      fontWeight: 'bold',
      color: '#243444'
    },
    stepTimeframe: {
      color: '#4C5454',
      fontSize: '0.9rem'
    },
    stepResources: {
      fontSize: '0.9rem',
      color: '#4C5454',
      fontStyle: 'italic'
    }
  };
  
  return (
    <div style={styles.container}>
      {/* Selector Bar */}
      <div style={styles.selectorBar}>
        <div style={styles.selectorContainer}>
          <CompactOrgSelector 
            onSelectOrganization={handleSelectOrganization} 
          />
          {selectedOrganization && (
            <CompactAssessmentSelector 
              organizationId={selectedOrganization._id} 
              selectedAssessment={selectedAssessment}
              onSelectAssessment={handleSelectAssessment}
            />
          )}
        </div>
      </div>
      
      {/* Main content */}
      <div style={styles.dashboardContent}>
      
      {/* Loading Modal for AI processing */}
      <LoadingModal 
        isOpen={aiLoading} 
        message="Generating industry insights from OpenAI. This may take a moment..." 
      />
      
      {loading ? (
        <div style={styles.loadingContainer}>
          <FaSpinner className="spinner-animation" style={styles.spinner} />
          <p>Loading industry analysis...</p>
          
          <style dangerouslySetInnerHTML={{ __html: `
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            .spinner-animation {
              animation: spin 1.5s linear infinite;
            }
            @media print {
              body * {
                visibility: hidden;
              }
              .printable, .printable * {
                visibility: visible;
              }
              .printable {
                position: absolute;
                left: 0;
                top: 0;
              }
            }
          `}} />
        </div>
      ) : error ? (
        <div style={styles.errorContainer}>
          <p>{error}</p>
          <button style={styles.button} onClick={() => navigate('/')}>
            Return to Dashboard
          </button>
        </div>
      ) : !selectedAssessment ? (
        <div style={styles.loadingContainer}>
          <p>Please select an organization and assessment from the dropdown selectors above to view industry insights.</p>
        </div>
      ) : (
        <>
          {/* Action Buttons removed to avoid duplication */}
          
          {/* Header with Export and Regenerate buttons */}
          {insightsGenerated && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px', gap: '15px' }}>
              <button 
                style={{...styles.button, backgroundColor: '#243444'}}
                onClick={handleRegenerateInsights}
                aria-label="Regenerate Results"
              >
                <FaSyncAlt style={{ marginRight: '8px' }} /> Regenerate Results
              </button>
              <button 
                style={styles.button}
                onClick={handleExportReport}
                aria-label="Export"
                disabled={exportLoading}
              >
                <FaFilePdf style={{ marginRight: '8px' }} /> Export
              </button>
            </div>
          )}
          
          {/* Exporting overlay */}
          {exportLoading && (
            <div style={styles.exportingOverlay}>
              <FaSpinner className="spinner-animation" style={{ fontSize: '40px', color: '#FFFAFA' }} />
              <p style={styles.exportingText}>Generating PDF report...</p>
            </div>
          )}
          {/* Detailed Analysis Section */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <FaChartLine style={styles.sectionIcon} />
              Detailed Analysis
            </h2>
            
            <div style={styles.card}>
              <p style={styles.analysisText}>
                {aiAnalysis.detailedAnalysis || 
                  (detailedInsights ? (
                    typeof detailedInsights.analysis === 'string' ? 
                      detailedInsights.analysis : 
                      "Your organization's resilience maturity is being analyzed. Please check back later."
                  ) : "Select an assessment to view detailed analysis.")}
              </p>
            </div>
          </section>
          
          {/* Industry Trends Section */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <FaIndustry style={styles.sectionIcon} />
              Industry Trends
            </h2>
            
            <div style={styles.grid}>
              <div style={styles.card}>
                <h3 style={styles.subsectionTitle}>Key Trends</h3>
                <ul style={styles.list}>
                  {(aiAnalysis.industryTrends || 
                    (industryData ? industryData.trends : [])).map((trend, index) => (
                    <li key={index} style={styles.listItem}>
                      <span style={styles.listItemIcon}>•</span>
                      <span>{trend}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div style={styles.card}>
                <h3 style={styles.subsectionTitle}>Key Industry Risks</h3>
                <ul style={styles.list}>
                  {(industryData ? industryData.keyRisks : []).map((risk, index) => (
                    <li key={index} style={styles.listItem}>
                      <span style={styles.listItemIcon}>•</span>
                      <span>{risk}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
          
          {/* Industry Comparison Section */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <FaChartBar style={styles.sectionIcon} />
              Industry Comparison
            </h2>
            
            {industryComparisons && (
              <>
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={styles.subsectionTitle}>Maturity Score Comparison</h3>
                  <p>
                    {aiAnalysis.industryComparison || 
                      `Compared to industry peers, the organization is significantly behind in resilience capabilities. Most companies in the ${selectedOrganization?.industryType || selectedAssessment?.industryType || 'universal'} sector have moved beyond initial maturity, often leveraging advanced technologies and comprehensive risk management strategies. The organization may face competitive disadvantages, especially in crisis response times and recovery capabilities, which could impact stakeholder trust and operational continuity.`}
                  </p>
                  
                  {/* Maturity Levels Visualization */}
                  <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                    <div style={styles.maturityLevelIndicator}>
                      {maturityLevels.map((level) => {
                        // Skip the 'None' level (index 0) in the visualization
                        if (level.level === 0) return null;
                        
                        // Check if this is the current maturity level
                        const isCurrentLevel = industryComparisons.overallComparison.organizationScore >= level.range[0] && 
                                              industryComparisons.overallComparison.organizationScore < level.range[1];
                        
                        return (
                          <div key={level.level} style={styles.maturityLevelItem}>
                            <div style={styles.maturityLevelDot(level.color, isCurrentLevel)}></div>
                            <div style={styles.maturityLevelLabel}>{level.name}</div>
                            <div style={styles.maturityLevelRange}>{level.range[0]}% - {level.range[1]-1}%</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div style={styles.comparisonBar}>
                    <div style={comparisonFill(industryComparisons.overallComparison.organizationScore)}></div>
                    <div style={comparisonMarker(industryComparisons.overallComparison.industryAverage)}></div>
                    <div style={comparisonLabel(industryComparisons.overallComparison.industryAverage)}>
                      Industry Avg: {industryComparisons.overallComparison.industryAverage}%
                    </div>
                  </div>
                  
                  <p>
                    Your organization is in the <strong>{industryComparisons.overallComparison.percentile}th</strong> percentile among {selectedOrganization?.industry || selectedAssessment?.industryType || 'Financial Services'} organizations.
                  </p>
                </div>
                
                <h3 style={styles.subsectionTitle}>Category Comparisons</h3>
                <div style={styles.grid}>
                  {industryComparisons.categoryComparisons.map((category, index) => (
                    <div key={index} style={styles.comparisonCard}>
                      <h4 style={styles.categoryTitle}>{category.category}</h4>
                      <div style={styles.categoryComparisonBar}>
                        {/* Industry Average Bar (gray) */}
                        <div 
                          style={{
                            ...styles.averageBar,
                            width: `${category.industryAverage}%`
                          }}
                        ></div>
                        {/* Assessment Score Bar (gold) */}
                        <div 
                          style={{
                            ...styles.assessmentBar,
                            width: `${category.score}%`
                          }}
                        ></div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px', fontSize: '0.8rem' }}>
                        <div>
                          <span style={{ color: '#D2BA7B', fontWeight: 'bold' }}>■</span> Your Score: {category.score}%
                        </div>
                        <div>
                          <span style={{ color: '#4C5454', fontWeight: 'bold' }}>■</span> Industry Avg: {category.industryAverage}%
                        </div>
                      </div>
                      <p style={{ 
                        color: category.isStrength ? '#4C5454' : '#7C1227', 
                        fontSize: '0.9rem',
                        marginTop: '5px'
                      }}>
                        {category.isStrength ? 'Strength' : 'Opportunity'}: {Math.abs(category.difference)}% {category.isStrength ? 'above' : 'below'} average
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
          
          {/* Improvement Opportunities Section */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <FaLightbulb style={styles.sectionIcon} />
              Improvement Opportunities
            </h2>
            
            <div style={styles.grid}>
              <div style={styles.card}>
                <h3 style={styles.subsectionTitle}>Top Areas for Improvement</h3>
                <ul style={styles.list}>
                  {(aiAnalysis.improvementOpportunities || 
                    (detailedInsights ? detailedInsights.improvementAreas : [])).map((area, index) => (
                    <li key={index} style={styles.listItem}>
                      <span style={styles.listItemIcon}>•</span>
                      <span>{area}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
          
          {/* Implementation Roadmap Section */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <FaRoad style={styles.sectionIcon} />
              Implementation Roadmap
            </h2>
            
            <p>Based on your organization's current maturity level, we recommend the following implementation steps:</p>
            
            <div style={{ marginTop: '20px' }}>
              {/* Process the roadmap data to ensure we have structured timeframes */}
              {(() => {
                // Initialize roadmap data structure
                const roadmapData = {
                  shortTerm: [],
                  mediumTerm: [],
                  longTerm: []
                };
                
                // Get the raw roadmap data
                const rawRoadmap = aiAnalysis.implementationRoadmap || 
                  (detailedInsights ? detailedInsights.implementationSteps : {});
                
                // Process the data based on its structure
                if (rawRoadmap) {
                  // If it's an object with timeframe properties (from OpenAI)
                  if (typeof rawRoadmap === 'object' && !Array.isArray(rawRoadmap) && 
                      (rawRoadmap.shortTerm || rawRoadmap.mediumTerm || rawRoadmap.longTerm)) {
                    
                    // Process each timeframe
                    ['shortTerm', 'mediumTerm', 'longTerm'].forEach(timeframe => {
                      if (rawRoadmap[timeframe]) {
                        // Extract steps from HTML content
                        const content = rawRoadmap[timeframe];
                        const steps = [];
                        
                        // Try to extract numbered items
                        const numberedItems = content.match(/\d+\.\s+([^\n]+)/g) || [];
                        
                        numberedItems.forEach(item => {
                          const stepText = item.replace(/^\d+\.\s+/, '').trim();
                          if (stepText) {
                            steps.push({
                              step: stepText,
                              timeframe: timeframe === 'shortTerm' ? 'Short Term (0-3 months)' : 
                                        timeframe === 'mediumTerm' ? 'Medium Term (3-12 months)' : 
                                        'Long Term (12+ months)',
                              resources: 'Varies',
                              category: timeframe
                            });
                          }
                        });
                        
                        roadmapData[timeframe] = steps;
                      }
                    });
                  } 
                  // If it's an array (from fallback or flattened data)
                  else if (Array.isArray(rawRoadmap)) {
                    // Distribute steps evenly across timeframes
                    const totalSteps = rawRoadmap.length;
                    const stepsPerTimeframe = Math.ceil(totalSteps / 3);
                    
                    // Process each step
                    rawRoadmap.forEach((step, index) => {
                      const stepObj = typeof step === 'string' 
                        ? { step, timeframe: 'Varies', resources: 'Varies' } 
                        : step;
                      
                      // Determine which timeframe this step belongs to
                      let category;
                      if (index < stepsPerTimeframe) {
                        category = 'shortTerm';
                        stepObj.timeframe = 'Short Term (0-3 months)';
                      } else if (index < stepsPerTimeframe * 2) {
                        category = 'mediumTerm';
                        stepObj.timeframe = 'Medium Term (3-12 months)';
                      } else {
                        category = 'longTerm';
                        stepObj.timeframe = 'Long Term (12+ months)';
                      }
                      
                      stepObj.category = category;
                      roadmapData[category].push(stepObj);
                    });
                  }
                }
                
                // Define default steps for each timeframe
                const defaultSteps = {
                  shortTerm: [
                    {
                      step: 'Conduct a thorough risk assessment to identify vulnerabilities and threats',
                      timeframe: 'Short Term (0-3 months)',
                      resources: 'Varies',
                      category: 'shortTerm'
                    },
                    {
                      step: 'Develop governance and strategic frameworks that align with organizational goals',
                      timeframe: 'Short Term (0-3 months)',
                      resources: 'Varies',
                      category: 'shortTerm'
                    },
                    {
                      step: 'Establish a crisis communication plan and notification procedures',
                      timeframe: 'Short Term (0-3 months)',
                      resources: 'Varies',
                      category: 'shortTerm'
                    },
                    {
                      step: 'Implement basic business continuity measures for critical functions',
                      timeframe: 'Short Term (0-3 months)',
                      resources: 'Varies',
                      category: 'shortTerm'
                    },
                    {
                      step: 'Create a resilience awareness program for key stakeholders',
                      timeframe: 'Short Term (0-3 months)',
                      resources: 'Varies',
                      category: 'shortTerm'
                    }
                  ],
                  mediumTerm: [
                    {
                      step: 'Develop comprehensive business continuity plans for all departments',
                      timeframe: 'Medium Term (3-12 months)',
                      resources: 'Medium',
                      category: 'mediumTerm'
                    },
                    {
                      step: 'Implement regular testing and exercises of response procedures',
                      timeframe: 'Medium Term (3-12 months)',
                      resources: 'Medium',
                      category: 'mediumTerm'
                    },
                    {
                      step: 'Establish metrics and reporting for resilience capabilities',
                      timeframe: 'Medium Term (3-12 months)',
                      resources: 'Medium',
                      category: 'mediumTerm'
                    },
                    {
                      step: 'Integrate resilience considerations into procurement and vendor management',
                      timeframe: 'Medium Term (3-12 months)',
                      resources: 'Medium',
                      category: 'mediumTerm'
                    },
                    {
                      step: 'Develop cross-functional resilience teams and define responsibilities',
                      timeframe: 'Medium Term (3-12 months)',
                      resources: 'Medium',
                      category: 'mediumTerm'
                    }
                  ],
                  longTerm: [
                    {
                      step: 'Integrate resilience into strategic planning and decision-making',
                      timeframe: 'Long Term (12+ months)',
                      resources: 'High',
                      category: 'longTerm'
                    },
                    {
                      step: 'Establish a continuous improvement program for resilience capabilities',
                      timeframe: 'Long Term (12+ months)',
                      resources: 'Medium',
                      category: 'longTerm'
                    },
                    {
                      step: 'Develop advanced scenario planning and horizon scanning capabilities',
                      timeframe: 'Long Term (12+ months)',
                      resources: 'High',
                      category: 'longTerm'
                    },
                    {
                      step: 'Create resilience innovation programs to address emerging risks',
                      timeframe: 'Long Term (12+ months)',
                      resources: 'High',
                      category: 'longTerm'
                    },
                    {
                      step: 'Build resilience partnerships with external stakeholders and industry groups',
                      timeframe: 'Long Term (12+ months)',
                      resources: 'Medium',
                      category: 'longTerm'
                    }
                  ]
                };
                
                // Ensure each timeframe has exactly 5 steps
                ['shortTerm', 'mediumTerm', 'longTerm'].forEach(timeframe => {
                  // If we have fewer than 5 steps, add default steps
                  while (roadmapData[timeframe].length < 5) {
                    const defaultIndex = roadmapData[timeframe].length;
                    if (defaultIndex < defaultSteps[timeframe].length) {
                      roadmapData[timeframe].push(defaultSteps[timeframe][defaultIndex]);
                    } else {
                      // If we've exhausted default steps, create a generic one
                      roadmapData[timeframe].push({
                        step: `Additional ${timeframe === 'shortTerm' ? 'short' : timeframe === 'mediumTerm' ? 'medium' : 'long'}-term action for business resilience`,
                        timeframe: timeframe === 'shortTerm' ? 'Short Term (0-3 months)' : 
                                  timeframe === 'mediumTerm' ? 'Medium Term (3-12 months)' : 
                                  'Long Term (12+ months)',
                        resources: 'Varies',
                        category: timeframe
                      });
                    }
                  }
                  
                  // If we have more than 5 steps, keep only the first 5
                  if (roadmapData[timeframe].length > 5) {
                    roadmapData[timeframe] = roadmapData[timeframe].slice(0, 5);
                  }
                });
                
                // Store the organized steps in the component state for use in the report
                if (!detailedInsights) {
                  setDetailedInsights(prevState => ({
                    ...prevState || {},
                    implementationSteps: [
                      ...roadmapData.shortTerm,
                      ...roadmapData.mediumTerm,
                      ...roadmapData.longTerm
                    ]
                  }));
                }
                
                // Render the roadmap sections
                return (
                  <>
                    {/* Short Term Section */}
                    <div style={{ marginBottom: '30px' }}>
                      <h3 style={{ 
                        color: '#243444', 
                        borderBottom: '2px solid #D2BA7B', 
                        paddingBottom: '8px',
                        marginBottom: '15px'
                      }}>
                        <span style={{ marginRight: '10px' }}>🔍</span>
                        Short Term Actions (0-3 months)
                      </h3>
                      
                      {roadmapData.shortTerm.map((stepObj, index) => (
                        <div 
                          key={`short-${index}`} 
                          style={{
                            ...styles.implementationStep,
                            cursor: 'pointer',
                            borderLeft: selectedStep && selectedStep.stepIdentifier === `shortTerm-${index}` 
                              ? '4px solid #D2BA7B' 
                              : '1px solid #e0e0e0',
                            backgroundColor: selectedStep && selectedStep.stepIdentifier === `shortTerm-${index}` 
                              ? '#f5f5f5' 
                              : '#f9f9f9'
                          }}
                          onClick={() => handleSelectStep(stepObj, index, 'shortTerm')}
                        >
                          <div style={styles.stepHeader}>
                            <div style={styles.stepTitle}>{index + 1}. {stepObj.step.replace(/^Step\s+\d+:\s*/i, '')}</div>
                            <div style={styles.stepTimeframe}>Timeframe: {stepObj.timeframe}</div>
                          </div>
                          <div style={styles.stepResources}>Resources needed: {stepObj.resources}</div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Medium Term Section */}
                    <div style={{ marginBottom: '30px' }}>
                      <h3 style={{ 
                        color: '#243444', 
                        borderBottom: '2px solid #D2BA7B', 
                        paddingBottom: '8px',
                        marginBottom: '15px'
                      }}>
                        <span style={{ marginRight: '10px' }}>⚙️</span>
                        Medium Term Actions (3-12 months)
                      </h3>
                      
                      {roadmapData.mediumTerm.map((stepObj, index) => (
                        <div 
                          key={`medium-${index}`} 
                          style={{
                            ...styles.implementationStep,
                            cursor: 'pointer',
                            borderLeft: selectedStep && selectedStep.stepIdentifier === `mediumTerm-${index}` 
                              ? '4px solid #D2BA7B' 
                              : '1px solid #e0e0e0',
                            backgroundColor: selectedStep && selectedStep.stepIdentifier === `mediumTerm-${index}` 
                              ? '#f5f5f5' 
                              : '#f9f9f9'
                          }}
                          onClick={() => handleSelectStep(stepObj, index, 'mediumTerm')}
                        >
                          <div style={styles.stepHeader}>
                            <div style={styles.stepTitle}>{index + 1}. {stepObj.step.replace(/^Step\s+\d+:\s*/i, '')}</div>
                            <div style={styles.stepTimeframe}>Timeframe: {stepObj.timeframe}</div>
                          </div>
                          <div style={styles.stepResources}>Resources needed: {stepObj.resources}</div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Long Term Section */}
                    <div>
                      <h3 style={{ 
                        color: '#243444', 
                        borderBottom: '2px solid #D2BA7B', 
                        paddingBottom: '8px',
                        marginBottom: '15px'
                      }}>
                        <span style={{ marginRight: '10px' }}>🚀</span>
                        Long Term Actions (12+ months)
                      </h3>
                      
                      {roadmapData.longTerm.map((stepObj, index) => (
                        <div 
                          key={`long-${index}`} 
                          style={{
                            ...styles.implementationStep,
                            cursor: 'pointer',
                            borderLeft: selectedStep && selectedStep.stepIdentifier === `longTerm-${index}` 
                              ? '4px solid #D2BA7B' 
                              : '1px solid #e0e0e0',
                            backgroundColor: selectedStep && selectedStep.stepIdentifier === `longTerm-${index}` 
                              ? '#f5f5f5' 
                              : '#f9f9f9'
                          }}
                          onClick={() => handleSelectStep(stepObj, index, 'longTerm')}
                        >
                          <div style={styles.stepHeader}>
                            <div style={styles.stepTitle}>{index + 1}. {stepObj.step.replace(/^Step\s+\d+:\s*/i, '')}</div>
                            <div style={styles.stepTimeframe}>Timeframe: {stepObj.timeframe}</div>
                          </div>
                          <div style={styles.stepResources}>Resources needed: {stepObj.resources}</div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          </section>
          
          {/* Items to Consider Section */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <FaLightbulb style={styles.sectionIcon} />
              Items to Consider
            </h2>
            
            <div style={styles.grid}>
              {(aiAnalysis.itemsToConsider || 
                (detailedInsights ? detailedInsights.itemsToConsider : [])).map((item, index) => (
                <div 
                  key={index} 
                  style={{
                    ...styles.card,
                    cursor: 'pointer',
                    borderLeft: selectedItem === item 
                      ? '4px solid #D2BA7B' 
                      : '1px solid #e0e0e0',
                    backgroundColor: selectedItem === item 
                      ? '#f5f5f5' 
                      : '#FFFAFA'
                  }}
                  onClick={() => handleSelectItem(item)}
                >
                  <h3 style={{ color: '#243444', fontSize: '16px', marginBottom: '10px' }}>{item}</h3>
                </div>
              ))}
            </div>
          </section>
          
          {/* Detail Panel for Implementation Steps and Items to Consider */}
          <div 
            style={{
              ...styles.detailPanel,
              ...(showStepDetails || showItemDetails ? {} : styles.detailPanelHidden)
            }}
          >
            {detailPanelContent && (
              <>
                <div style={styles.detailPanelHeader}>
                  <h3 style={styles.detailPanelTitle}>
                    {detailPanelContent.title}
                  </h3>
                  <button 
                    style={styles.closeButton} 
                    onClick={closeDetailPanel}
                    aria-label="Close detail panel"
                  >
                    <FaTimes />
                  </button>
                </div>
                
                <div style={styles.detailPanelSection}>
                  <p style={styles.detailPanelDescription}>
                    {detailPanelContent.description}
                  </p>
                  
                  {showStepDetails && (
                    <div style={styles.detailPanelMetadata}>
                      <div style={styles.detailPanelMetadataItem}>
                        <span style={styles.detailPanelMetadataLabel}>Timeframe</span>
                        <span style={styles.detailPanelMetadataValue}>
                          <FaClock style={{ marginRight: '5px', fontSize: '0.9rem' }} />
                          {detailPanelContent.timeframe}
                        </span>
                      </div>
                      <div style={styles.detailPanelMetadataItem}>
                        <span style={styles.detailPanelMetadataLabel}>Resources</span>
                        <span style={styles.detailPanelMetadataValue}>{detailPanelContent.resources}</span>
                      </div>
                    </div>
                  )}
                </div>
                
                <div style={styles.detailPanelSection}>
                  <h4 style={styles.detailPanelSectionTitle}>
                    <FaRoad style={styles.detailPanelSectionIcon} />
                    Implementation Steps
                  </h4>
                  <ul style={styles.list}>
                    {(showStepDetails ? detailPanelContent.implementation : detailPanelContent.implementation).map((item, index) => (
                      <li key={index} style={styles.listItem}>
                        <span style={styles.listItemIcon}>•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div style={styles.detailPanelSection}>
                  <h4 style={styles.detailPanelSectionTitle}>
                    <FaExclamationTriangle style={styles.detailPanelSectionIcon} />
                    Potential Challenges
                  </h4>
                  <ul style={styles.list}>
                    {(showStepDetails ? detailPanelContent.potentialChallenges : detailPanelContent.potentialChallenges).map((item, index) => (
                      <li key={index} style={styles.listItem}>
                        <span style={styles.listItemIcon}>•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div style={styles.detailPanelSection}>
                  <h4 style={styles.detailPanelSectionTitle}>
                    <FaLightbulb style={styles.detailPanelSectionIcon} />
                    {showStepDetails ? 'Key Benefits' : 'Benefits'}
                  </h4>
                  <ul style={styles.list}>
                    {(showStepDetails ? detailPanelContent.keyBenefits : detailPanelContent.benefits).map((item, index) => (
                      <li key={index} style={styles.listItem}>
                        <span style={styles.listItemIcon}>•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </div>
          
          {/* Regulatory Considerations Section */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <FaBook style={styles.sectionIcon} />
              Industry-Specific Regulatory Considerations
            </h2>
            
            <div style={styles.card}>
              {aiLoading ? (
                <div style={styles.loadingContainer}>
                  <FaSpinner style={styles.spinner} />
                  <p>Analyzing industry regulations...</p>
                </div>
              ) : (
                <>
                  {selectedAssessment && selectedOrganization ? (
                    <div>
                      {aiAnalysis.regulatoryConsiderations ? (
                        <div>
                          {typeof aiAnalysis.regulatoryConsiderations === 'object' ? (
                            <>
                              <p style={styles.analysisText}>{aiAnalysis.regulatoryConsiderations.overview}</p>
                              
                              {aiAnalysis.regulatoryConsiderations.keyRegulations && (
                                <div style={{marginTop: '15px'}}>
                                  <h4 style={{color: '#243444', marginBottom: '10px', fontSize: '16px'}}>Key Regulations & Compliance Frameworks</h4>
                                  <ul style={{paddingLeft: '20px'}}>
                                    {Array.isArray(aiAnalysis.regulatoryConsiderations.keyRegulations) ? (
                                      aiAnalysis.regulatoryConsiderations.keyRegulations.map((reg, index) => (
                                        <li key={index} style={{marginBottom: '5px'}}>{reg}</li>
                                      ))
                                    ) : (
                                      <li>{aiAnalysis.regulatoryConsiderations.keyRegulations}</li>
                                    )}
                                  </ul>
                                </div>
                              )}
                              
                              {aiAnalysis.regulatoryConsiderations.recentChanges && (
                                <div style={{marginTop: '15px'}}>
                                  <h4 style={{color: '#243444', marginBottom: '10px', fontSize: '16px'}}>Recent & Upcoming Regulatory Changes</h4>
                                  <p style={styles.analysisText}>{aiAnalysis.regulatoryConsiderations.recentChanges}</p>
                                </div>
                              )}
                              
                              {aiAnalysis.regulatoryConsiderations.complianceRequirements && (
                                <div style={{marginTop: '15px'}}>
                                  <h4 style={{color: '#243444', marginBottom: '10px', fontSize: '16px'}}>Compliance Requirements</h4>
                                  <p style={styles.analysisText}>{aiAnalysis.regulatoryConsiderations.complianceRequirements}</p>
                                </div>
                              )}
                              
                              {aiAnalysis.regulatoryConsiderations.bestPractices && (
                                <div style={{marginTop: '15px'}}>
                                  <h4 style={{color: '#243444', marginBottom: '10px', fontSize: '16px'}}>Best Practices</h4>
                                  <p style={styles.analysisText}>{aiAnalysis.regulatoryConsiderations.bestPractices}</p>
                                </div>
                              )}
                              
                              {aiAnalysis.regulatoryConsiderations.regulatoryBodies && (
                                <div style={{marginTop: '15px'}}>
                                  <h4 style={{color: '#243444', marginBottom: '10px', fontSize: '16px'}}>Regulatory Bodies & Authorities</h4>
                                  <ul style={{paddingLeft: '20px'}}>
                                    {Array.isArray(aiAnalysis.regulatoryConsiderations.regulatoryBodies) ? (
                                      aiAnalysis.regulatoryConsiderations.regulatoryBodies.map((body, index) => (
                                        <li key={index} style={{marginBottom: '5px'}}>{body}</li>
                                      ))
                                    ) : (
                                      <li>{aiAnalysis.regulatoryConsiderations.regulatoryBodies}</li>
                                    )}
                                  </ul>
                                </div>
                              )}
                              
                              {aiAnalysis.regulatoryConsiderations.industrySpecificGuidance && (
                                <div style={{marginTop: '15px'}}>
                                  <h4 style={{color: '#243444', marginBottom: '10px', fontSize: '16px'}}>Industry-Specific Guidance</h4>
                                  <p style={styles.analysisText}>{aiAnalysis.regulatoryConsiderations.industrySpecificGuidance}</p>
                                </div>
                              )}
                            </>
                          ) : (
                            <p style={styles.analysisText}>{aiAnalysis.regulatoryConsiderations}</p>
                          )}
                        </div>
                      ) : detailedInsights?.regulatoryConsiderations ? (
                        <p style={styles.analysisText}>{detailedInsights.regulatoryConsiderations}</p>
                      ) : (
                        <p style={styles.analysisText}>
                          Based on your organization's industry ({selectedOrganization.industry || 'Not specified'}), the AI will provide specific regulatory considerations that may impact your business resilience planning.
                        </p>
                      )}
                    </div>
                  ) : (
                    <p style={styles.analysisText}>
                      Select an assessment to view regulatory considerations specific to your industry. The AI will analyze your industry type and provide relevant regulatory frameworks, compliance requirements, and best practices.
                    </p>
                  )}
                  
                  {selectedAssessment && !aiAnalysis.regulatoryConsiderations && !detailedInsights?.regulatoryConsiderations && (
                    <button 
                      onClick={handleRegenerateInsights}
                      style={styles.regenerateButton}
                      disabled={aiLoading}
                    >
                      <FaSyncAlt style={{ marginRight: '8px' }} />
                      Generate Regulatory Analysis
                    </button>
                  )}
                </>
              )}
            </div>
          </section>
          
          <button style={styles.button} onClick={() => navigate('/')}>
            <span style={{ marginRight: '10px' }}>Return to Dashboard</span>
          </button>
        </>
      )}
      </div>
    </div>
  );
};

export default IndustryAnalysisPage;
